import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { format } from "date-fns";
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Button,
  Stack,
  SwipeableDrawer,
  Fab,
  useMediaQuery,
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  DirectionsCar as CarIcon,
  Speed as SpeedIcon,
  Battery20 as BatteryIcon,
  Wifi as WifiIcon,
  VpnKey as KeyIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
  LinkOff as LinkOffIcon,
  AccessTime as AccessTimeIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import {
  decryptLiveLocationData,
  LiveLocationData,
  isLiveLocationExpired,
  getExpiryTimeMs,
} from "../utils/encryption";
import "leaflet/dist/leaflet.css";

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface Device {
  id: number;
  name: string;
  unique_id: string;
  status: string;
  phone: string;
  model: string;
  category: string;
  attributes: {
    ignition: boolean;
    charge: boolean;
    blocked: boolean;
    battery_level: string;
    status: string;
    device_online: boolean;
    latest_speed?: number;
    odometer?: number;
    ac?: boolean;
    port: any;
    last_updated_at: any;
    // Extended fields available from API
    today_distance?: number;
    total_distance?: number;
    total_running?: number; // seconds
    today_idle_time?: number; // seconds
    total_idle?: number; // seconds
    total_stopped?: number;
  };
  latest_position?: {
    gps_data: {
      latitude: number;
      longitude: number;
      address?: string;
      course_status?: {
        course?: number;
      };
      battery_power: any;
    };
    updated_at: number;
    protocol?: string;
  };
  latest_heartbeat?: {
    gps_data: {
      voltage_level: string;
      external_voltage_value?: number;
      terminal_info?: {
        oil_electricity_disconnected: boolean;
      };
      battery_power: any;
    };
    updated_at: number;
  };
}

// Component to update map center when location changes
const MapUpdater: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center, 15);
  }, [center, map]);

  return null;
};

const PublicLiveTracking: React.FC = () => {
  const { encryptedData } = useParams<{ encryptedData: string }>();
  const navigate = useNavigate();

  const [device, setDevice] = useState<Device | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [liveData, setLiveData] = useState<LiveLocationData | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width:900px)");

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const expiryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helpers similar to VehicleTracking
  const getVehicleIconUrl = (deviceData?: Device) => {
    const currentDevice = deviceData || device;
    if (!currentDevice) return "/car/white.png";

    const lastUpdateTime = Math.max(
      currentDevice?.latest_position?.updated_at || 0,
      currentDevice?.latest_heartbeat?.updated_at || 0
    );
    const currentTime = Date.now();
    const timeDiffInHours = (currentTime - lastUpdateTime) / (1000 * 60 * 60);

    if (timeDiffInHours > 4) return "/car/blue.png"; // Inactive
    if (
      currentDevice?.attributes?.latest_speed &&
      currentDevice.attributes.latest_speed > 0
    )
      return "/car/green.png"; // Running
    if (currentDevice?.attributes?.status === "idle") return "/car/orange.png"; // Idle
    if (currentDevice?.attributes?.status === "stopped") return "/car/red.png"; // Stopped
    return "/car/white.png"; // Default
  };

  const statusIcon = React.useMemo(() => {
    return new L.Icon({
      iconUrl: getVehicleIconUrl(device || undefined),
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    device?.attributes?.status,
    device?.attributes?.latest_speed,
    device?.latest_position?.updated_at,
    device?.latest_heartbeat?.updated_at,
  ]);

  const formatDurationFromSeconds = (seconds?: number | null) => {
    if (!seconds || seconds <= 0) return "0h 0m";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Decrypt URL data
  useEffect(() => {
    if (!encryptedData) {
      setError("Invalid tracking link");
      setLoading(false);
      return;
    }

    try {
      const decryptedData = decryptLiveLocationData(encryptedData);
      setLiveData(decryptedData);
      setIsExpired(
        isLiveLocationExpired({
          issuedAt: decryptedData.issuedAt,
          timeFrame: decryptedData.timeFrame,
        })
      );
      // Schedule auto-expiry while user is on page
      if (expiryTimeoutRef.current) {
        clearTimeout(expiryTimeoutRef.current as unknown as number);
        expiryTimeoutRef.current = null;
      }
      const expiryAt = getExpiryTimeMs({
        issuedAt: decryptedData.issuedAt,
        timeFrame: decryptedData.timeFrame,
      });
      const delay = Math.max(0, expiryAt - Date.now());
      expiryTimeoutRef.current = setTimeout(() => {
        setIsExpired(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current as unknown as number);
          intervalRef.current = null;
        }
      }, delay);
    } catch (err) {
      setError("Invalid or corrupted tracking link");
      setLoading(false);
    }
    return () => {
      if (expiryTimeoutRef.current) {
        clearTimeout(expiryTimeoutRef.current as unknown as number);
        expiryTimeoutRef.current = null;
      }
    };
  }, [encryptedData]);

  // Fetch device data
  const fetchDeviceData = async (isInitial = false) => {
    if (!liveData) return;

    if (isInitial) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);

    try {
      // Get auth token from localStorage or use a public endpoint
      const userData = localStorage.getItem("user");
      let authToken = "";

      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          authToken = parsedUser["X-AUTHORIZATION-TOKEN"] || "";
        } catch (parseError) {}
      }

      // For public access, we might need a different endpoint or token
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}gpstracker/devicehandler/`,
        {
          headers: { "X-AUTHORIZATION-TOKEN": liveData.userToken },
        }
      );

      if (response.data?.responseData) {
        let currentDevice = response.data.responseData.find(
          (device: any) => device.id === liveData.deviceId
        );
        setDevice(currentDevice);
        setLastUpdate(new Date());
      }
    } catch (err: any) {
      setError("Failed to load vehicle data");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (liveData) {
      // If expired, do not fetch
      const expired = isLiveLocationExpired({
        issuedAt: liveData.issuedAt,
        timeFrame: liveData.timeFrame,
      });
      setIsExpired(expired);
      if (expired) {
        setLoading(false);
        return;
      }
      fetchDeviceData(true);
    }
  }, [liveData]);

  // Set up auto-refresh
  useEffect(() => {
    if (!liveData) return;
    const expired = isLiveLocationExpired({
      issuedAt: liveData.issuedAt,
      timeFrame: liveData.timeFrame,
    });
    setIsExpired(expired);
    if (expired) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      // Stop polling immediately if expired mid-session
      if (
        isLiveLocationExpired({
          issuedAt: liveData.issuedAt,
          timeFrame: liveData.timeFrame,
        })
      ) {
        setIsExpired(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current as unknown as number);
          intervalRef.current = null;
        }
        return;
      }
      fetchDeviceData(false);
    }, 10000); // Refresh every 10 seconds

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (expiryTimeoutRef.current) {
        clearTimeout(expiryTimeoutRef.current as unknown as number);
        expiryTimeoutRef.current = null;
      }
    };
  }, [liveData]);

  // Get vehicle status
  const getDeviceStatus = () => {
    if (!device) return "No Data";
    if (device.latest_heartbeat === null && device.latest_position === null)
      return "No Data";

    const lastUpdateTime = Math.max(
      device?.latest_position?.updated_at || 0,
      device?.latest_heartbeat?.updated_at || 0
    );
    const currentTime = Date.now();
    const timeDiffInHours = (currentTime - lastUpdateTime) / (1000 * 60 * 60);

    if (timeDiffInHours > 4) return "Inactive";
    if (device?.attributes?.latest_speed && device.attributes.latest_speed > 0)
      return "Running";
    if (device?.attributes?.status === "idle") return "Idle";
    if (device?.attributes?.status === "stopped") return "Stopped";
    return "No Data";
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "running":
        return "#43A047";
      case "stopped":
        return "#E53935";
      case "idle":
        return "#FFA000";
      case "inactive":
        return "#2196F3";
      default:
        return "#757575";
    }
  };

  const getBatteryColor = (device: Device) => {
    const voltageLevelRaw = device?.latest_heartbeat?.gps_data?.voltage_level;
    const batteryPowerRaw = device?.latest_heartbeat?.gps_data?.battery_power;

    let percent: number | null = null;

    if (typeof voltageLevelRaw === "string") {
      const trimmed = voltageLevelRaw.trim();
      if (trimmed.endsWith("%")) {
        const numericPart = parseFloat(trimmed.slice(0, -1));
        if (!isNaN(numericPart)) {
          percent = numericPart;
        }
      }
    }

    if (percent === null) {
      const batteryNumeric = Number(batteryPowerRaw);
      if (!isNaN(batteryNumeric)) {
        percent = batteryNumeric;
      }
    }

    if (percent === null) {
      return { color: "#E53935", backgroundColor: "#FFEBEE" };
    }

    const isHealthy = percent > 10;
    return {
      color: isHealthy ? "#43A047" : "#E53935",
      backgroundColor: isHealthy ? "#E8F5E9" : "#FFEBEE",
    };
  };

  // Reusable details panel content
  const VehicleDetailsPanel: React.FC<{ currentDevice: Device; lastUpdateAt: Date | null }> = ({
    currentDevice,
    lastUpdateAt,
  }) => {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Vehicle Details
        </Typography>

        {/* Status Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
            mb: 2,
          }}
        >
          {/* Speed */}
          <Card sx={{ bgcolor: "#f8fafc" }}>
            <CardContent sx={{ p: 1.5, textAlign: "center" }}>
              <SpeedIcon sx={{ color: "#48bb78", mb: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                Speed
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#48bb78" }}>
                {currentDevice.attributes?.latest_speed || 0} km/h
              </Typography>
            </CardContent>
          </Card>

          {/* Battery */}
          <Card sx={{ bgcolor: "#f8fafc" }}>
            <CardContent sx={{ p: 1.5, textAlign: "center" }}>
              <BatteryIcon sx={{ color: getBatteryColor(currentDevice).color, mb: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                Battery
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: getBatteryColor(currentDevice).color,
                }}
              >
                {typeof currentDevice?.latest_heartbeat?.gps_data?.voltage_level === "string" &&
                currentDevice?.latest_heartbeat?.gps_data?.voltage_level
                  .trim()
                  .endsWith("%")
                  ? currentDevice?.latest_heartbeat?.gps_data?.voltage_level.trim()
                  : `${currentDevice?.latest_heartbeat?.gps_data?.battery_power ?? 0}%`}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Summary Cards (like VehicleTracking) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
            mb: 2,
          }}
        >
          {/* Today Distance */}
          <Card sx={{ bgcolor: "#f8fafc" }}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Today Distance
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#4299e1" }}>
                {currentDevice.attributes?.today_distance
                  ? `${currentDevice.attributes.today_distance.toFixed(1)} KM`
                  : "0.0 KM"}
              </Typography>
            </CardContent>
          </Card>

          {/* Total Distance / Odometer */}
          <Card sx={{ bgcolor: "#f8fafc" }}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Total Odometer
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#4a5568" }}>
                {currentDevice.attributes?.total_distance
                  ? `${currentDevice.attributes.total_distance.toFixed(0)} KM`
                  : `${(currentDevice.attributes?.odometer ?? 0).toFixed?.(0) || 0} KM`}
              </Typography>
            </CardContent>
          </Card>

          {/* Running Time */}
          <Card sx={{ bgcolor: "#f8fafc" }}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Running Time
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#38a169" }}>
                {formatDurationFromSeconds(currentDevice.attributes?.total_running)}
              </Typography>
            </CardContent>
          </Card>

          {/* Idle Time (today) */}
          <Card sx={{ bgcolor: "#f8fafc" }}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Today Idle Time
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#ed8936" }}>
                {formatDurationFromSeconds(currentDevice.attributes?.today_idle_time)}
              </Typography>
            </CardContent>
          </Card>

          {/* Total Idle */}
          <Card sx={{ bgcolor: "#f8fafc" }}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Total Idle
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#ed8936" }}>
                {formatDurationFromSeconds(currentDevice.attributes?.total_idle)}
              </Typography>
            </CardContent>
          </Card>

          {/* Stops */}
          <Card sx={{ bgcolor: "#f8fafc" }}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Total Stops
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#e53e3e" }}>
                {currentDevice.attributes?.total_stopped ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Additional Info */}
        <Box sx={{ space: 1 }}>
          {/* GPS Status */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <WifiIcon
              sx={{
                color: currentDevice?.latest_heartbeat ? "#48bb78" : "#e53e3e",
                mr: 1,
                fontSize: 16,
              }}
            />
            <Typography variant="body2">
              GPS: {currentDevice?.latest_heartbeat ? "Connected" : "Disconnected"}
            </Typography>
          </Box>

          {/* Ignition Status */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <KeyIcon
              sx={{
                color: currentDevice?.attributes?.ignition ? "#48bb78" : "#e53e3e",
                mr: 1,
                fontSize: 16,
              }}
            />
            <Typography variant="body2">
              Ignition: {currentDevice?.attributes?.ignition ? "On" : "Off"}
            </Typography>
          </Box>

          {/* Last Update */}
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Last Updated: {lastUpdateAt ? formatLastUpdatedAt(currentDevice?.attributes?.last_updated_at) : "Never"}
          </Typography>
        </Box>
      </Box>
    );
  };

  const formatLastUpdatedAt = (timestamp: any) => {
    if (!timestamp) return "-";
    return format(new Date(timestamp), "dd:MM:yyyy");
  };

  const handleRefresh = () => {
    fetchDeviceData(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100dvh",
          bgcolor: "#f8fafc",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#1976d2" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box sx={{ display: "flex", gap: 2 }}>
          <button
            onClick={() => navigate("/")}
            style={{
              background: "#f7fafc",
              border: "1px solid #e2e8f0",
              color: "#4a5568",
              padding: "8px 16px",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            <ArrowBackIcon sx={{ mr: 1, fontSize: 16 }} />
            Go Home
          </button>
        </Box>
      </Box>
    );
  }

  if (isExpired && liveData) {
    const expiry = new Date(
      getExpiryTimeMs({
        issuedAt: liveData.issuedAt,
        timeFrame: liveData.timeFrame,
      })
    );
    return (
      <Box
        sx={{
          minHeight: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 520,
            width: "100%",
            p: 4,
            textAlign: "center",
            borderRadius: 3,
            border: "1px solid #e2e8f0",
            bgcolor: "white",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <LinkOffIcon sx={{ fontSize: 48, color: "#f59e0b" }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1f2937", mb: 1 }}>
            Link expired
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This public live tracking link is no longer active.
          </Typography>

          <Alert severity="warning" sx={{ mb: 3, textAlign: "left" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTimeIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                Expired at {expiry.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: true })}
              </Typography>
            </Box>
          </Alert>

        </Paper>
      </Box>
    );
  }

  if (!device || !device.latest_position?.gps_data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No location data available for this vehicle
        </Alert>
        <button
          onClick={() => navigate("/")}
          style={{
            background: "#f7fafc",
            border: "1px solid #e2e8f0",
            color: "#4a5568",
            padding: "8px 16px",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          <ArrowBackIcon sx={{ mr: 1, fontSize: 16 }} />
          Go Home
        </button>
      </Box>
    );
  }

  const currentPosition: [number, number] = [
    device.latest_position.gps_data.latitude,
    device.latest_position.gps_data.longitude,
  ];

  return (
    <Box sx={{ height: "100dvh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: "white",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1a202c" }}>
              {device.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Live Location Tracking
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={getDeviceStatus()}
            sx={{
              bgcolor: getStatusColor(getDeviceStatus()),
              color: "white",
              fontWeight: 500,
            }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshIcon sx={{ color: isRefreshing ? "#ccc" : "#4a5568" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: "flex", position: "relative" }}>
        {/* Map */}
        <Box sx={{ flex: 1, position: "relative" }}>
          <MapContainer
            center={currentPosition}
            zoom={15}
            style={{ height: "100%", width: "100%" }}
            zoomControl={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <MapUpdater center={currentPosition} />
            <Marker position={currentPosition} icon={statusIcon}>
              <Popup>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {device.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Status: {getDeviceStatus()}
                  </Typography>
                  {device.attributes?.latest_speed && (
                    <Typography variant="body2" color="text.secondary">
                      Speed: {device.attributes.latest_speed} km/h
                    </Typography>
                  )}
                  {device.latest_position.gps_data.address && (
                    <Typography variant="body2" color="text.secondary">
                      {device.latest_position.gps_data.address}
                    </Typography>
                  )}
                </Box>
              </Popup>
            </Marker>
          </MapContainer>
          {isSmallScreen && (
            <Fab
              color="primary"
              size="medium"
              onClick={() => setIsDetailsOpen(true)}
              sx={{ position: "absolute", right: 16, bottom: 16 }}
            >
              <InfoOutlinedIcon />
            </Fab>
          )}
        </Box>
        {!isSmallScreen && (
          <Paper
            sx={{
              width: 320,
              height: "100%",
              borderRadius: 0,
              borderLeft: "1px solid #e2e8f0",
              overflow: "auto",
            }}
          >
            <VehicleDetailsPanel currentDevice={device} lastUpdateAt={lastUpdate} />
          </Paper>
        )}
      </Box>

      {/* Mobile Bottom Sheet for Details */}
      {isSmallScreen && (
        <SwipeableDrawer
          anchor="bottom"
          open={isDetailsOpen}
          onOpen={() => setIsDetailsOpen(true)}
          onClose={() => setIsDetailsOpen(false)}
          PaperProps={{
            sx: {
              maxHeight: "75vh",
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              pb: "max(env(safe-area-inset-bottom), 8px)",
            },
          }}
          ModalProps={{ keepMounted: true }}
        >
          <Box sx={{ pt: 1, pb: 2, px: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 4,
                  borderRadius: 9999,
                  bgcolor: "#CBD5E1",
                }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {device.name}
              </Typography>
              <Chip
                label={getDeviceStatus()}
                size="small"
                sx={{ bgcolor: getStatusColor(getDeviceStatus()), color: "white", fontWeight: 500 }}
              />
            </Box>
          </Box>
          <Divider />
          <VehicleDetailsPanel currentDevice={device} lastUpdateAt={lastUpdate} />
        </SwipeableDrawer>
      )}
    </Box>
  );
};

export default PublicLiveTracking;
